import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import { alumniList, alumniAdd, alumniEnrich, alumniEnrichAll, alumniOutreach, alumniDelete } from "@/lib/campusApi";
import { Upload, Phone, ChevronRight } from "lucide-react";
import {
  GraduationCap, Loader2, Plus, X, Sparkles, Search, Trash2, Linkedin,
  Building2, RefreshCw, Mail, Copy, MapPin, Briefcase,
} from "lucide-react";

const AV_HUES = ["#C2410C", "#0a66c2", "#059669", "#7c3aed", "#db2777", "#0891b2", "#ca8a04", "#4f46e5"];
function companyHue(name) {
  let h = 0; for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) % AV_HUES.length;
  return AV_HUES[h];
}
function CompanyAvatar({ name, size = 34 }) {
  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  return (
    <span style={{ width: size, height: size, background: companyHue(name), fontSize: size * 0.4 }}
      className="inline-flex items-center justify-center rounded-xl text-white font-bold shrink-0">{initials}</span>
  );
}

/* AI Alumni Network — upload alumni LinkedIn URLs, the AI enriches each
   profile's background (current + past companies), aggregates "where our
   alumni work", and drafts warm outreach (recruit / refer / mentor). */

export default function AlumniPage() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ company: "", batch_year: "", branch: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [batchBusy, setBatchBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = (f = filters) => {
    const params = {};
    if (f.company.trim()) params.company = f.company.trim();
    if (f.batch_year) params.batch_year = Number(f.batch_year);
    if (f.branch.trim()) params.branch = f.branch.trim();
    alumniList(params).then(({ data }) => setData(data)).catch((e) => setMsg(e?.response?.data?.detail || "Could not load alumni."));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const enrichOne = async (a) => {
    setBusyId(a.alumni_id); setMsg("");
    try { await alumniEnrich(a.alumni_id); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Enrich failed."); }
    finally { setBusyId(null); }
  };
  const enrichAll = async () => {
    setBatchBusy(true); setMsg("");
    try { const { data: r } = await alumniEnrichAll(); setMsg(`Enriched ${r.enriched}, failed ${r.failed}, still pending ${r.still_pending} (batch of ${r.batch_cap} per run — cost control).`); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Batch enrich failed."); }
    finally { setBatchBusy(false); }
  };
  const del = async (a) => {
    if (!(await confirm({ title: "Please confirm", description: `Remove ${a.name || a.linkedin_url} from the alumni network?`, danger: true }))) return;
    await alumniDelete(a.alumni_id); load();
  };

  if (!data) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading alumni network…</div>;
  const { stats } = data;

  return (
    <div className="container-x py-10 md:py-14" data-testid="alumni-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="overline">AI Alumni Network</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Your alumni, mapped.</h1>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">Upload alumni LinkedIn profiles — the AI pulls their career history so your cell knows exactly which companies employ your graduates, and drafts the warm outreach that opens those doors.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          {stats.pending > 0 && (
            <button onClick={enrichAll} disabled={batchBusy} data-testid="alumni-enrich-all"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 disabled:opacity-60">
              {batchBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Enrich {Math.min(stats.pending, 10)} pending
            </button>
          )}
          <button onClick={() => setShowAdd(true)} data-testid="alumni-add" className="btn-primary text-sm"><Plus size={15} /> Add alumni</button>
        </div>
      </div>

      {/* Stats + company map */}
      <div className="grid grid-cols-3 gap-3 mt-8 max-w-md">
        {[["Alumni", stats.total], ["Enriched", stats.enriched], ["Pending", stats.pending]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-center">
            <p className="font-display text-2xl">{v}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">{l}</p>
          </div>
        ))}
      </div>
      {stats.top_companies?.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Where your alumni work — click a company to filter</p>
            {filters.company && (
              <button onClick={() => { const f = { ...filters, company: "" }; setFilters(f); load(f); }} className="text-xs text-[#C2410C] font-medium hover:underline">Clear filter ×</button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {stats.top_companies.map((c) => {
              const active = filters.company === c.company;
              return (
                <button key={c.company} onClick={() => { const f = { ...filters, company: active ? "" : c.company }; setFilters(f); load(f); }}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-2xl border text-left transition-all ${active ? "border-[#C2410C] bg-amber-50 ring-1 ring-[#C2410C]" : "border-stone-200 bg-white hover:border-stone-400"}`}>
                  <CompanyAvatar name={c.company} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900 truncate">{c.company}</p>
                    <p className="text-xs text-stone-500">{c.alumni} alumni</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {msg && <p className="mt-4 text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2.5">{msg}</p>}

      {/* Alumni cards */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-8">
        {data.alumni.map((a) => {
          const e = a.enriched || {};
          return (
            <div key={a.alumni_id} className="rounded-3xl border border-stone-200 bg-white p-5 flex flex-col hover:shadow-sm transition-shadow" data-testid={`alumni-${a.alumni_id}`}>
              <div className="flex items-start gap-3">
                <CompanyAvatar name={e.current_company || a.seed_company || (e.name || a.name)} size={44} />
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg truncate leading-tight">{e.name || a.name || "Unknown alumnus"}</p>
                  <p className="text-xs text-stone-500 truncate">{[a.branch, a.batch_year && `Batch ${a.batch_year}`].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                {a.linkedin_url && <a href={a.linkedin_url} target="_blank" rel="noreferrer" className="shrink-0 p-1.5 rounded-lg text-[#0a66c2] hover:bg-blue-50"><Linkedin size={15} /></a>}
              </div>

              {a.status === "enriched" ? (
                <div className="mt-3.5 flex-1">
                  <div className="rounded-2xl bg-stone-50 border border-stone-100 px-3 py-2.5">
                    <p className="text-sm text-stone-900 font-medium leading-snug">{e.current_role || "—"}</p>
                    <p className="text-xs text-[#C2410C] font-medium flex items-center gap-1 mt-0.5"><Building2 size={11} /> {e.current_company || "—"}</p>
                    {e.location && <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><MapPin size={10} /> {e.location}</p>}
                  </div>
                  {(e.companies || []).length > 1 && (
                    <div className="mt-3">
                      <p className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mb-1.5">Career path</p>
                      <div className="space-y-1.5">
                        {(e.companies || []).slice(0, 4).map((c, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            <CompanyAvatar name={c.name} size={20} />
                            <span className="text-stone-700 truncate flex-1"><b>{c.name}</b>{c.role ? ` · ${c.role}` : ""}</span>
                            {c.years && <span className="text-stone-400 shrink-0">{c.years}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : a.status === "failed" ? (
                <div className="mt-3 flex-1">
                  {a.seed_company && <p className="text-sm text-stone-700 flex items-center gap-1.5"><Building2 size={12} className="text-stone-400" /> {a.seed_company}</p>}
                  <p className="text-xs text-red-600 mt-1">{a.fail_reason || "Enrichment failed."}</p>
                </div>
              ) : (
                <div className="mt-3 flex-1">
                  {a.seed_company
                    ? <p className="text-sm text-stone-700 flex items-center gap-1.5"><Building2 size={12} className="text-stone-400" /> {a.seed_company} <span className="text-stone-400">(unverified)</span></p>
                    : <p className="text-xs text-stone-400">Not enriched yet — fetch their LinkedIn background.</p>}
                  {(a.email || a.phone) && (
                    <div className="mt-2 space-y-0.5 text-xs text-stone-500">
                      {a.email && <p className="flex items-center gap-1.5 truncate"><Mail size={10} /> {a.email}</p>}
                      {a.phone && <p className="flex items-center gap-1.5"><Phone size={10} /> {a.phone}</p>}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-2">
                {a.status !== "enriched" && (
                  <button onClick={() => enrichOne(a)} disabled={busyId === a.alumni_id} data-testid={`alumni-enrich-${a.alumni_id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-[#C2410C] disabled:opacity-60">
                    {busyId === a.alumni_id ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Enrich
                  </button>
                )}
                {a.status === "enriched" && (
                  <button onClick={() => setViewing(a)} data-testid={`alumni-outreach-${a.alumni_id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#C2410C] text-white hover:bg-[#9a3412]">
                    <Mail size={12} /> Draft outreach
                  </button>
                )}
                <button onClick={() => del(a)} className="ml-auto p-1.5 rounded-lg text-stone-300 hover:text-red-500 hover:bg-red-50"><Trash2 size={13} /></button>
              </div>
            </div>
          );
        })}
        {data.alumni.length === 0 && (
          <div className="col-span-3 text-center py-16 text-stone-400">
            <GraduationCap size={40} className="mx-auto mb-4 opacity-30" />
            <p>No alumni yet — add LinkedIn profiles to build your network map.</p>
          </div>
        )}
      </div>

      {showAdd && <AddAlumniModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {viewing && <OutreachModal alum={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

const EMPTY = { linkedin_url: "", name: "", batch_year: "", branch: "", email: "", phone: "", company: "" };

const AL_EMAIL_RE = /[^\s,;]+@[^\s,;]+\.[^\s,;]+/;

// Tolerant parser for pasted rows / Excel-CSV. Columns (header optional, any order):
// name, linkedin (url), batch/year, branch, email, phone, company.
function parseAlumni(text) {
  if (text.startsWith("PK") || /[\u0000-\u0008]/.test(text)) return { rows: [], binary: true };
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], binary: false };
  const sample = lines[0];
  const delim = [";", "\t", ","].reduce((b, d) => sample.split(d).length > sample.split(b).length ? d : b, ",");
  const split = (l) => l.split(delim).map((x) => x.trim().replace(/^"(.*)"$/, "$1"));
  const first = split(lines[0]).map((c) => c.toLowerCase());
  const looksHeader = first.some((c) => ["name", "linkedin", "url", "batch", "year", "branch", "email", "phone", "company"].includes(c));
  const body = looksHeader ? lines.slice(1) : lines;
  const find = (...names) => first.findIndex((c) => names.some((n) => c.includes(n)));
  const idx = looksHeader
    ? { name: find("name"), url: find("linkedin", "url", "profile"), batch: find("batch", "year"), branch: find("branch", "dept"), email: find("email", "mail"), phone: find("phone", "mobile"), company: find("company", "employer", "organisation", "organization") }
    : { name: 0, url: 1, batch: 2, branch: 3, email: -1, phone: -1, company: -1 };
  const rows = [];
  for (const line of body) {
    const c = split(line);
    const get = (k) => (idx[k] >= 0 && idx[k] < c.length ? c[k === "name" ? idx.name : idx[k]] : "");
    const cell = (n) => (n >= 0 && n < c.length ? c[n] : "");
    let url = cell(idx.url); if (!/linkedin\.com/i.test(url)) { const f = c.find((x) => /linkedin\.com/i.test(x)); if (f) url = f; }
    let email = cell(idx.email); if (!AL_EMAIL_RE.test(email)) { const f = c.find((x) => AL_EMAIL_RE.test(x)); email = f || ""; }
    let name = cell(idx.name);
    if (!name || /linkedin\.com/i.test(name) || AL_EMAIL_RE.test(name)) {
      name = url ? "" : (c.find((x) => x && !/linkedin\.com/i.test(x) && !AL_EMAIL_RE.test(x)) || "");
    }
    const batch = Number(cell(idx.batch));
    const row = {};
    if (/linkedin\.com/i.test(url)) row.linkedin_url = url.trim();
    if (name) row.name = name.trim();
    if (Number.isInteger(batch) && batch >= 1950 && batch <= 2035) row.batch_year = batch;
    if (cell(idx.branch) && !/linkedin\.com/i.test(cell(idx.branch))) row.branch = cell(idx.branch).trim();
    if (AL_EMAIL_RE.test(email)) row.email = email.trim();
    if (cell(idx.phone) && /\d/.test(cell(idx.phone)) && !AL_EMAIL_RE.test(cell(idx.phone))) row.phone = cell(idx.phone).trim();
    if (cell(idx.company)) row.company = cell(idx.company).trim();
    if (row.linkedin_url || row.name) rows.push(row);
  }
  return { rows, binary: false };
}

function AddAlumniModal({ onClose, onSaved }) {
  const [tab, setTab] = useState("bulk"); // bulk | manual
  const [text, setText] = useState("");
  const [rows, setRows] = useState([{ ...EMPTY }]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const set = (i, k) => (e) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: e.target.value } : row)));

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (/\.xlsx?$/i.test(f.name)) { setErr("That's an Excel file — in Excel use File → Save As → CSV, then upload the .csv (or just copy the rows and paste below)."); return; }
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || "")); setErr(""); };
    reader.readAsText(f);
  };

  const submitBulk = async () => {
    const { rows: parsed, binary } = parseAlumni(text);
    if (binary) { setErr("This looks like a binary/Excel file. Save it as CSV first, or paste the rows as text."); return; }
    if (!parsed.length) { setErr("No usable rows found. One alumnus per line: name, LinkedIn URL, batch, branch, email, phone, company."); return; }
    setSaving(true); setErr("");
    try { const { data } = await alumniAdd(parsed); toast.success(`Added ${data.added}, skipped ${data.skipped} (duplicates/blank).`); onSaved?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to add."); }
    finally { setSaving(false); }
  };

  const submitManual = async () => {
    const payload = rows.map((r) => ({
      linkedin_url: r.linkedin_url.trim() || undefined, name: r.name.trim() || undefined,
      batch_year: r.batch_year ? Number(r.batch_year) : undefined, branch: r.branch.trim() || undefined,
      email: r.email.trim() || undefined, phone: r.phone.trim() || undefined, company: r.company.trim() || undefined,
    })).filter((r) => r.linkedin_url || r.name);
    if (!payload.length) { setErr("Add at least a LinkedIn URL or a name."); return; }
    setSaving(true); setErr("");
    try { const { data } = await alumniAdd(payload); toast.success(`Added ${data.added}, skipped ${data.skipped} (duplicates/blank).`); onSaved?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to add."); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-2xl rounded-3xl bg-white p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">Add alumni</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>

        <div className="mt-4 inline-flex rounded-full bg-stone-100 p-1 text-sm">
          <button onClick={() => setTab("bulk")} data-testid="al-tab-bulk" className={`px-4 py-1.5 rounded-full font-medium transition-colors ${tab === "bulk" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>Bulk upload</button>
          <button onClick={() => setTab("manual")} data-testid="al-tab-manual" className={`px-4 py-1.5 rounded-full font-medium transition-colors ${tab === "manual" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>Add manually</button>
        </div>

        {tab === "bulk" ? (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-stone-600">Upload a <code>.csv</code> (from Excel: <b>Save As → CSV</b>) or paste rows below. Columns — a header row is recommended:</p>
            <p className="font-mono text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">name, linkedin, batch, branch, email, phone, company</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-sm cursor-pointer hover:bg-stone-100 w-fit">
              <Upload size={14} /> Choose CSV file
              <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" data-testid="al-file" />
            </label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} data-testid="al-bulk-text"
              placeholder={"name,linkedin,batch,branch,email,phone,company\nRhea Nair,linkedin.com/in/rhea-nair,2019,CSE,rhea@ex.com,,Google\nArjun Rao,linkedin.com/in/arjun-rao,2020,ECE,,,Amazon"}
              className="w-full px-3 py-2.5 rounded-xl border border-stone-300 text-xs font-mono leading-relaxed focus:outline-none focus:border-stone-900 resize-y" />
            {err && <p className="text-sm text-red-600">{err}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-sm">Cancel</button>
              <button onClick={submitBulk} disabled={saving} data-testid="al-bulk-save" className="btn-primary text-sm disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Import alumni
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <p className="text-xs text-stone-500 mb-3">A LinkedIn URL lets the AI enrich the full career history; without one, at least add a name + company.</p>
            <div className="space-y-3">
              {rows.map((r, i) => (
                <div key={i} className="rounded-2xl border border-stone-200 p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input value={r.linkedin_url} onChange={set(i, "linkedin_url")} placeholder="linkedin.com/in/…" data-testid={`al-url-${i}`}
                      className="col-span-2 px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono focus:outline-none focus:border-stone-900" />
                    <input value={r.name} onChange={set(i, "name")} placeholder="Name" className="px-3 py-2 rounded-xl border border-stone-300 text-sm" />
                    <input value={r.company} onChange={set(i, "company")} placeholder="Current company" className="px-3 py-2 rounded-xl border border-stone-300 text-sm" />
                    <input type="number" value={r.batch_year} onChange={set(i, "batch_year")} placeholder="Batch (2019)" className="px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono" />
                    <input value={r.branch} onChange={set(i, "branch")} placeholder="Branch" className="px-3 py-2 rounded-xl border border-stone-300 text-sm" />
                    <input value={r.email} onChange={set(i, "email")} placeholder="Email (optional)" className="px-3 py-2 rounded-xl border border-stone-300 text-sm" />
                    <input value={r.phone} onChange={set(i, "phone")} placeholder="Phone (optional)" className="px-3 py-2 rounded-xl border border-stone-300 text-sm" />
                  </div>
                  {rows.length > 1 && <button onClick={() => setRows((x) => x.filter((_, j) => j !== i))} className="mt-2 text-[10px] text-red-500 hover:underline">Remove</button>}
                </div>
              ))}
              <button onClick={() => setRows((r) => [...r, { ...EMPTY }])} className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 border border-stone-300 rounded-full px-3.5 py-1.5 hover:border-stone-900">
                <Plus size={12} /> Another alumnus
              </button>
            </div>
            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-sm">Cancel</button>
              <button onClick={submitManual} disabled={saving} data-testid="al-save" className="btn-primary text-sm disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add to network
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OutreachModal({ alum, onClose }) {
  const [purpose, setPurpose] = useState("recruit");
  const [draft, setDraft] = useState(alum.outreach_draft || null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const generate = async (p = purpose) => {
    setBusy(true); setErr("");
    try { const { data } = await alumniOutreach(alum.alumni_id, p); setDraft(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Draft failed."); }
    finally { setBusy(false); }
  };
  const copy = () => {
    navigator.clipboard?.writeText(`Subject: ${draft.subject}\n\n${draft.body}`).then(() => toast.success("Copied — paste into email or LinkedIn message."));
  };
  const e = alum.enriched || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(ev) => { if (ev.target === ev.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-xl rounded-3xl bg-white p-6 max-h-[88vh] overflow-y-auto" data-testid="alumni-outreach-modal">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl">{e.name || alum.name}</h2>
            <p className="text-xs text-stone-500">{e.current_role} at {e.current_company}{alum.batch_year ? ` · Batch ${alum.batch_year}` : ""}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>

        <div className="mt-4 flex gap-2">
          {[["recruit", "Invite to recruit"], ["refer", "Ask for referrals"], ["mentor", "Invite to mentor"]].map(([p, label]) => (
            <button key={p} onClick={() => { setPurpose(p); generate(p); }} disabled={busy}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors disabled:opacity-60 ${purpose === p ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
              {label}
            </button>
          ))}
        </div>

        {busy ? <p className="mt-5 text-sm text-stone-500 flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Writing a warm, personal note…</p>
          : err ? <p className="mt-5 text-sm text-red-600">{err}</p>
          : draft ? (
            <div className="mt-5">
              <p className="text-xs font-medium text-stone-500">Subject</p>
              <p className="text-sm font-medium text-stone-900 mt-0.5">{draft.subject}</p>
              <pre className="mt-3 text-sm text-stone-700 whitespace-pre-wrap font-sans leading-relaxed rounded-2xl bg-stone-50 border border-stone-200 p-4">{draft.body}</pre>
              <button onClick={copy} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full bg-stone-900 text-white hover:bg-[#C2410C]">
                <Copy size={12} /> Copy message
              </button>
            </div>
          ) : <button onClick={() => generate()} className="mt-5 btn-primary text-sm"><Sparkles size={14} /> Generate draft</button>}
      </div>
    </div>
  );
}
